import { useEffect, useState } from "react";
import * as Icons from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const REACT_BE = process.env.REACT_APP_BACKEND_URL;

export default function WebChatSettings() {
  const [data, setData] = useState(null);
  const [activeFirm, setActiveFirm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [analytics, setAnalytics] = useState(null);
  const [range, setRange] = useState(30);

  const load = () => api.get("/widget/admin/settings").then((r) => {
    setData(r.data);
    if (!activeFirm) setActiveFirm(Object.keys(r.data.settings.firms)[0]);
  }).catch(() => toast.error("Failed to load WebChat settings"));
  useEffect(() => { load(); }, []); // eslint-disable-line
  useEffect(() => {
    if (!activeFirm) return;
    api.get(`/widget/admin/analytics?firm_id=${activeFirm}&days=${range}`).then((r) => setAnalytics(r.data)).catch(() => {});
  }, [activeFirm, range]);

  if (!data) return <div className="p-6"><Icons.Loader2 className="h-5 w-5 animate-spin text-blue-500" /></div>;
  const s = data.settings;
  const firms = s.firms || {};
  const firm = firms[activeFirm] || {};

  const setFirm = (patch) => setData((d) => ({ ...d, settings: { ...d.settings, firms: { ...d.settings.firms, [activeFirm]: { ...d.settings.firms[activeFirm], ...patch } } } }));
  const setTop = (patch) => setData((d) => ({ ...d, settings: { ...d.settings, ...patch } }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/widget/admin/settings", {
        enabled: s.enabled, penny_enabled: s.penny_enabled, escalation_sla_minutes: Number(s.escalation_sla_minutes),
        consult_free_minutes: Number(s.consult_free_minutes), consult_simple_rate: Number(s.consult_simple_rate),
        consult_complex_rate: Number(s.consult_complex_rate), required_fields: s.required_fields, firms: s.firms,
        new_visitor_gap_minutes: Number(s.new_visitor_gap_minutes),
        penny_avatar_url: s.penny_avatar_url, penny_tagline: s.penny_tagline,
      });
      toast.success("WebChat settings saved");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); } finally { setSaving(false); }
  };

  const copyEmbed = () => { navigator.clipboard.writeText(data.embed_scripts[activeFirm] || ""); toast.success("Embed script copied"); };
  const inp = "w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-100";
  const lbl = "text-xs font-medium text-slate-500 mb-1 block";

  return (
    <div className="p-6 max-w-[1100px] mx-auto space-y-6" data-testid="webchat-settings">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">WebChat Widget</h1>
          <p className="text-sm text-slate-500">Penny-powered chat widget for all Tax Accounting Network websites.</p>
        </div>
        <div className="flex items-center gap-3">
          <a href={`${REACT_BE}/widget?firm=${activeFirm}&open=1`} target="_blank" rel="noreferrer" data-testid="webchat-preview"
            className="inline-flex items-center gap-2 text-sm border border-slate-200 rounded-lg px-3 py-2 hover:bg-slate-50 text-slate-700"><Icons.ExternalLink className="h-4 w-4" />Preview</a>
          <button onClick={save} disabled={saving} data-testid="webchat-save" className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded-lg px-4 py-2 font-medium disabled:opacity-50">
            {saving ? <Icons.Loader2 className="h-4 w-4 animate-spin" /> : <Icons.Save className="h-4 w-4" />}Save
          </button>
        </div>
      </div>

      {/* Global toggles */}
      <div className="bg-white border border-slate-200 rounded-xl p-5 grid grid-cols-2 md:grid-cols-5 gap-4">
        <Toggle label="Widget Enabled" checked={s.enabled} onChange={(v) => setTop({ enabled: v })} testid="webchat-enabled" />
        <Toggle label="Penny Enabled" checked={s.penny_enabled} onChange={(v) => setTop({ penny_enabled: v })} testid="webchat-penny-enabled" />
        <div><label className={lbl}>Escalation SLA (min)</label><input className={inp} type="number" value={s.escalation_sla_minutes} onChange={(e) => setTop({ escalation_sla_minutes: e.target.value })} data-testid="webchat-sla" /></div>
        <div><label className={lbl}>Free Consult (min)</label><input className={inp} type="number" value={s.consult_free_minutes} onChange={(e) => setTop({ consult_free_minutes: e.target.value })} /></div>
        <div><label className={lbl}>New-Visitor Gap (min) <span className="text-slate-400 font-normal">anti-spam</span></label><input className={inp} type="number" value={s.new_visitor_gap_minutes ?? 360} onChange={(e) => setTop({ new_visitor_gap_minutes: e.target.value })} data-testid="webchat-visitor-gap" /></div>
      </div>

      {/* Penny persona */}
      <div className="bg-white border border-slate-200 rounded-xl p-5 flex items-center gap-4" data-testid="webchat-penny-persona">
        <img src={s.penny_avatar_url} alt="Penny" className="h-16 w-16 rounded-full object-cover border border-slate-200 shrink-0" />
        <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div><label className={lbl}>Penny Avatar URL</label><input className={inp} value={s.penny_avatar_url || ""} onChange={(e) => setTop({ penny_avatar_url: e.target.value })} data-testid="webchat-avatar-url" /></div>
          <div><label className={lbl}>Penny Tagline</label><input className={inp} value={s.penny_tagline || ""} onChange={(e) => setTop({ penny_tagline: e.target.value })} data-testid="webchat-tagline" /></div>
        </div>
      </div>

      {/* Firm tabs */}
      <div className="flex gap-1.5 flex-wrap">
        {Object.keys(firms).map((fid) => (
          <button key={fid} onClick={() => setActiveFirm(fid)} data-testid={`webchat-firm-${fid}`}
            className={`text-sm px-3.5 py-2 rounded-lg border flex items-center gap-2 ${activeFirm === fid ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200"}`}>
            <img src={firms[fid].logo_url} alt="" className="h-5 w-5 object-contain rounded" />{firms[fid].brand_name}
          </button>
        ))}
      </div>

      {/* Analytics funnel */}
      <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid="webchat-analytics">
        <div className="flex items-center justify-between mb-4">
          <div className="text-sm font-semibold text-slate-800 flex items-center gap-2"><Icons.BarChart3 className="h-4 w-4 text-blue-600" />Conversion Funnel — {firm.brand_name}</div>
          <select value={range} onChange={(e) => setRange(Number(e.target.value))} data-testid="webchat-analytics-range"
            className="text-xs border border-slate-300 rounded-lg px-2 py-1.5">
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
            <option value={3650}>All time</option>
          </select>
        </div>
        {!analytics ? <div className="text-sm text-slate-400 py-6 text-center">Loading analytics…</div> : (
          <div className="space-y-2.5">
            {analytics.funnel.map((f, i) => (
              <div key={f.stage} className="flex items-center gap-3" data-testid={`funnel-${f.stage}`}>
                <div className="w-40 text-xs text-slate-600 font-medium shrink-0">{i + 1}. {f.label}</div>
                <div className="flex-1 bg-slate-100 rounded-full h-7 overflow-hidden relative">
                  <div className="h-full rounded-full transition-all duration-500 flex items-center justify-end pr-2"
                    style={{ width: `${Math.max(f.pct, f.count > 0 ? 6 : 0)}%`, background: firm.primary_color || "#2563eb" }}>
                    {f.count > 0 && <span className="text-[11px] font-bold text-white">{f.count}</span>}
                  </div>
                  {f.count === 0 && <span className="absolute left-2 top-1/2 -translate-y-1/2 text-[11px] text-slate-400">0</span>}
                </div>
                <div className="w-12 text-right text-xs font-semibold text-slate-500 shrink-0">{f.pct}%</div>
              </div>
            ))}
            <div className="flex gap-4 pt-3 mt-1 border-t border-slate-100 text-xs text-slate-500">
              <span>Contacts captured: <b className="text-slate-800">{analytics.extra?.contact_captured || 0}</b></span>
              <span>Quotes: <b className="text-slate-800">{analytics.extra?.quote_requested || 0}</b></span>
              <span>Escalations: <b className="text-slate-800">{analytics.extra?.escalated || 0}</b></span>
            </div>
          </div>
        )}
      </div>

      {/* Firm branding */}
      <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div><label className={lbl}>Brand Name</label><input className={inp} value={firm.brand_name || ""} onChange={(e) => setFirm({ brand_name: e.target.value })} data-testid="webchat-brand-name" /></div>
          <div><label className={lbl}>Specialty</label><input className={inp} value={firm.specialty || ""} onChange={(e) => setFirm({ specialty: e.target.value })} /></div>
          <div><label className={lbl}>Logo URL</label><input className={inp} value={firm.logo_url || ""} onChange={(e) => setFirm({ logo_url: e.target.value })} data-testid="webchat-logo-url" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className={lbl}>Primary Color</label><div className="flex gap-2"><input type="color" value={firm.primary_color || "#2563eb"} onChange={(e) => setFirm({ primary_color: e.target.value })} className="h-9 w-12 rounded border border-slate-300" /><input className={inp} value={firm.primary_color || ""} onChange={(e) => setFirm({ primary_color: e.target.value })} /></div></div>
            <div><label className={lbl}>Accent Color</label><div className="flex gap-2"><input type="color" value={firm.accent_color || "#1f2a44"} onChange={(e) => setFirm({ accent_color: e.target.value })} className="h-9 w-12 rounded border border-slate-300" /><input className={inp} value={firm.accent_color || ""} onChange={(e) => setFirm({ accent_color: e.target.value })} /></div></div>
          </div>
          <div><label className={lbl}>Launcher Text</label><input className={inp} value={firm.launcher_text || ""} onChange={(e) => setFirm({ launcher_text: e.target.value })} /></div>
          <div><label className={lbl}>Default Team</label><input className={inp} value={firm.default_team || ""} onChange={(e) => setFirm({ default_team: e.target.value })} /></div>
        </div>
        <div><label className={lbl}>Greeting Bubble Text <span className="text-slate-400 font-normal">(pops from the launcher after ~8s)</span></label><input className={inp} value={firm.greeting_text || ""} onChange={(e) => setFirm({ greeting_text: e.target.value })} data-testid="webchat-greeting-text" placeholder="e.g. Living abroad? Ask Penny about expat taxes." /></div>
        <div><label className={lbl}>Welcome Message</label><textarea className={inp} rows={2} value={firm.welcome_message || ""} onChange={(e) => setFirm({ welcome_message: e.target.value })} data-testid="webchat-welcome" /></div>
      </div>

      {/* Embed script */}
      <div className="bg-slate-900 rounded-xl p-5">
        <div className="flex items-center justify-between mb-3">
          <div className="text-sm font-semibold text-slate-100 flex items-center gap-2"><Icons.Code className="h-4 w-4" />WordPress Embed Script — {firm.brand_name}</div>
          <button onClick={copyEmbed} data-testid="webchat-copy-embed" className="inline-flex items-center gap-1.5 text-xs bg-slate-700 hover:bg-slate-600 text-white rounded-lg px-3 py-1.5"><Icons.Copy className="h-3.5 w-3.5" />Copy</button>
        </div>
        <pre className="text-[11px] leading-relaxed text-emerald-300 whitespace-pre-wrap font-mono overflow-x-auto" data-testid="webchat-embed-code">{data.embed_scripts[activeFirm]}</pre>
      </div>
    </div>
  );
}

function Toggle({ label, checked, onChange, testid }) {
  return (
    <div>
      <label className="text-xs font-medium text-slate-500 mb-1 block">{label}</label>
      <button onClick={() => onChange(!checked)} data-testid={testid}
        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${checked ? "bg-emerald-500" : "bg-slate-300"}`}>
        <span className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${checked ? "translate-x-5" : "translate-x-0.5"}`} />
      </button>
    </div>
  );
}
